import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useServices } from "@/app/use-services";
import { ApiError, errorMessage } from "@/lib/api/errors";
import type { LifeIndex, LifeRow } from "@/lib/api/models/life";
import { ErrorView } from "@/ui/ErrorView";
import { Page } from "@/ui/Page";
import { Row } from "@/ui/Row";
import { Body, Display, Label } from "@/ui/Text";
import { Paywall } from "@/features/plan/Paywall";
import { outcomeFor, wallForOutcome, type WallCopy } from "@/features/plan/paywall-copy";
import { LIFE_INDEX_QUERY_KEY, NO_PROFILE_LINE } from "./keys";


const FREE_THINGS = [
  { path: "/life/charts", row: "All sixteen of your charts" },
  { path: "/life/transits", row: "Where the planets are now" },
  { path: "/life/remedies", row: "Remedies" },
] as const;


/** The questions people actually have, and the free things underneath. */
export function LifeTab() {
  const { api, analytics } = useServices();
  const navigate = useNavigate();
  const [wall, setWall] = useState<WallCopy | null>(null);
  const index = useQuery<LifeIndex>({ queryKey: LIFE_INDEX_QUERY_KEY, queryFn: () => api.fetchLifeIndex() });

  function open(row: LifeRow) {
    analytics.track({ name: "reading opened", reading: row.reading });
    if (row.locked) {
      const outcome = outcomeFor(row.reading);
      if (outcome) setWall(wallForOutcome(outcome));
      return;
    }
    navigate(`/life/${row.reading}`);
  }

  return (
    <Page>
      <Label>Life</Label>
      <Display size="md" className="mt-2.5">
        The questions you actually have.
      </Display>
      <div className="mt-1.5">
        <IndexRows query={index} onOpen={open} />
      </div>
      <Label className="mt-5">Free, always</Label>
      {FREE_THINGS.map((thing) => (
        <Row key={thing.path} value={thing.row} onClick={() => navigate(thing.path)} />
      ))}
      <Paywall copy={wall} onClose={() => { setWall(null); void index.refetch(); }} />
    </Page>
  );
}

function IndexRows({ query, onOpen }: { readonly query: ReturnType<typeof useQuery<LifeIndex>>; readonly onOpen: (row: LifeRow) => void }) {
  if (query.isPending) return <Body className="mt-3">Reading what your chart can answer…</Body>;
  if (query.isError) {
    if (query.error instanceof ApiError && query.error.isNotFound) return <Body className="mt-3">{NO_PROFILE_LINE}</Body>;
    return <ErrorView message={errorMessage(query.error)} onRetry={() => void query.refetch()} />;
  }
  if (query.data.rows.length === 0) return <Body className="mt-3">There are no readings to show yet. Try again in a moment.</Body>;
  return (
    <>
      {query.data.rows.map((row) => (
        <Row key={row.reading} value={row.title} note={row.subtitle || undefined} onClick={() => onOpen(row)} />
      ))}
    </>
  );
}
